import { enhancedApi } from '@store/generated/patients';

/**
 * Tag types for patient document caches. We also declare cross-cutting
 * 'Document' / 'DocumentList' tags here so the patient-scoped views can
 * invalidate the org-wide documents list when a patient upload settles
 * (and vice versa). Tags share a single namespace across all RTK Query
 * slices derived from baseApi, so listing them here is purely a
 * type-safety opt-in.
 */
const PATIENT_DOCUMENTS_TAG_TYPES = [
  'PatientDocument',
  'PatientDocumentsList',
  'Document',
  'DocumentList',
] as const;

export const patientDocumentsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: PATIENT_DOCUMENTS_TAG_TYPES,
  endpoints: {
    getV2ProviderPatientDocumentsByPatientIdList: {
      providesTags: (result, _error, { pathPatientId }) =>
        result
          ? [
              ...result.items.map(item => ({
                type: 'PatientDocument' as const,
                id: item.document_id,
              })),
              { type: 'PatientDocumentsList' as const, id: pathPatientId },
              // Generic 'LIST' marker so cross-feature invalidations (e.g.
              // a PATCH from DocumentDetailModal) can hit every mounted
              // patient docs list without knowing which patient it's for.
              { type: 'PatientDocumentsList' as const, id: 'LIST' },
            ]
          : [
              { type: 'PatientDocumentsList' as const, id: pathPatientId },
              { type: 'PatientDocumentsList' as const, id: 'LIST' },
            ],

      // SSE for this list moved to `usePatientArtifactEventStream` —
      // mounted by callers only when they actually need updates (e.g.
      // while there are pending uploads). The previous
      // `onCacheEntryAdded` here keyed connections by every filter/page
      // arg, so active filtering would stack overlapping SSEs for 60s
      // each. The hook approach gives one connection per patient,
      // closed promptly when no longer needed.
    },
  },
});

// Export hooks with user-friendly names only
export const {
  useGetV2ProviderPatientDocumentsByPatientIdListQuery: useGetPatientDocumentsQuery,
  useGetV2ProviderPatientDocumentsByDocumentIdDownloadUrlQuery: useGetDocumentDownloadUrlQuery,
  useLazyGetV2ProviderPatientDocumentsByDocumentIdDownloadUrlQuery: useLazyGetDocumentDownloadUrlQuery,
} = patientDocumentsApi;
